import React, { useState } from "react";
import styled from "@emotion/styled";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import ServiceWrapper from "../components/ServiceWrapper";
import BizInfo from "../components/BizInfo";
import PhoneCertModal from "../components/PhoneCertModal";
import auth from "../utils/auth";
import { track } from "../utils/track";
import { isApp, startCardSubscription, useBilling } from "../utils/billing";
import { useScoreCount } from "../utils/scoreCount";

/**
 * 구독 안내 (2026-10). 로그인하지 않아도 볼 수 있는 상품 페이지 — 전자결제(토스페이먼츠) 심사의
 * '판매 상품 1개 이상' 조건과, 처음 온 사람에게 요금·혜택·해지·환불을 알리는 용도.
 */
const PRICE = 3300;

const FEATURES = [
  "마디 보기 · 가사 보기(노래방식) · 박자 표시",
  "키 바꾸기 · 쉬운 코드(카포) · 코드 단순하게",
  "기타 반주 · 메트로놈 · 탭 템포",
  "연주 체크와 점수 · 원곡 영상 같이 보기",
  "신청곡 악보 제작 (악보 신청)",
];

const Pricing: React.FC = () => {
  const history = useHistory();
  const scoreCount = useScoreCount();
  const billing = useBilling(); // 카드 자동결제가 켜진 회원이면 정보, 아니면 null (예전 구독 신청 방식)
  const status = auth.getPayStatus();
  // 카드 자동결제 회원이면 카드 구독 상태로만 판단 (시험 계정도 카드 등록을 해 볼 수 있게)
  const subscribed = billing
    ? !!(billing.subscription && ["active", "past_due", "canceled"].includes(billing.subscription.status))
    : status === "1" || status === "0" || status === "4";

  const [askPhone, setAskPhone] = useState(false);

  const start = async () => {
    track("pricing:start");
    if (!auth.getToken()) history.push("/auth/login");
    else if (billing && !isApp()) {
      // 카드 등록 뒤 첫 결제는 휴대폰 번호가 있어야 되므로, 번호 없는 회원은 먼저 받는다
      if (!auth.getPhone()) setAskPhone(true);
      else await startCardSubscription(billing); // 토스 카드 등록창 → 첫 결제
    } else history.push("/membership");
  };

  return (
    <ServiceWrapper isFooterRendered={false}>
      <Page>
        <h1>코드스코어 구독</h1>
        <Lead>치고 싶은 노래, 코드 악보로 바로.</Lead>

        <Plan className="p-plan">
          <PlanHead>
            <span>월 구독</span>
            <Price>
              <b>{PRICE.toLocaleString()}원</b>
              <small>/ 월 (부가세 포함)</small>
            </Price>
          </PlanHead>
          <ul>
            {[`${scoreCount}곡이 넘는 코드 악보를 모두 열람`, ...FEATURES].map((f) => (
              <li key={f}>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l4 4 10-10" /></svg>
                {f}
              </li>
            ))}
          </ul>
          <Period>
            서비스 제공기간 : 결제일로부터 1개월
            <br />
            해지하지 않으면 매월 같은 날 자동으로 결제되어 1개월씩 이어집니다.
          </Period>
          <Start type="button" onClick={start} disabled={subscribed}>
            {subscribed ? "이미 구독 중이에요" : auth.getToken() ? "구독 시작하기" : "로그인하고 구독하기"}
          </Start>
        </Plan>

        <Notes className="p-notes">
          <h2>결제 · 해지 · 환불 안내</h2>
          <ul>
            <li>첫 결제가 끝나면 바로 모든 악보를 볼 수 있어요. 이후 매월 같은 날 자동으로 결제됩니다.</li>
            <li>언제든 메뉴('내 정보')의 '구독 해지'에서 해지할 수 있고, 해지해도 이미 결제한 기간이 끝날 때까지 이용할 수 있어요.</li>
            <li>결제일로부터 7일 안에 유료 악보를 열람하지 않았다면 전액 환불해 드려요. 열람한 뒤에는 그달 요금은 환불되지 않고, 다음 달부터 청구되지 않아요.</li>
            <li>환불·결제 문의는 <Link to="/support">문의하기</Link>로 남겨 주세요.</li>
          </ul>
          <p>
            자세한 내용은 <Link to="/policy/service">이용약관</Link>을 확인해 주세요.
          </p>
        </Notes>

        <BizInfo />
      </Page>
      {askPhone && billing && (
        <PhoneCertModal
          onClose={() => setAskPhone(false)}
          onDone={async () => {
            setAskPhone(false);
            await startCardSubscription(billing);
          }}
        />
      )}
    </ServiceWrapper>
  );
};

export default Pricing;

const Page = styled.div`
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 24px 20px 110px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  color: #f3f4f6;
  font-family: "Noto Sans KR", "Apple SD Gothic Neo", sans-serif;
  & > h1 {
    margin: 0;
    font-size: 26px;
    font-weight: 800;
  }
  @media (min-width: 700px) {
    padding: 40px 32px 60px;
  }
  @media (min-width: 1024px) {
    max-width: 1080px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 48px;
    align-items: start;
    & > h1,
    & > p,
    & > footer {
      grid-column: 1 / -1;
    }
    & > .p-notes {
      padding-top: 8px;
    }
  }
`;

const Lead = styled.p`
  margin: -12px 0 0;
  font-size: 15px;
  color: #a3a9b5;
`;

const Plan = styled.section`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 22px 20px;
  border-radius: 20px;
  background: #16181e;
  border: 1px solid #3a3220;
  & > ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  & li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 15px;
    line-height: 1.45;
    word-break: keep-all;
  }
  & li > svg {
    flex: none;
    width: 18px;
    height: 18px;
    margin-top: 2px;
    fill: none;
    stroke: #ffb547;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const PlanHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  & > span {
    font-size: 13px;
    color: #ffb547;
    font-weight: 700;
  }
`;

const Price = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  & > b {
    font-size: 34px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }
  & > small {
    font-size: 14px;
    color: #a3a9b5;
  }
`;

const Period = styled.p`
  margin: 0;
  padding: 12px 14px;
  border-radius: 12px;
  background: #1f222a;
  font-size: 14px;
  line-height: 1.6;
  color: #d5d9e0;
`;

const Start = styled.button`
  height: 54px;
  border: 0;
  border-radius: 14px;
  background: #ffb547;
  color: #0d0e12;
  font-size: 17px;
  font-weight: 800;
  cursor: pointer;
  &:disabled {
    background: #2a2e38;
    color: #a3a9b5;
    cursor: default;
  }
`;

const Notes = styled.section`
  font-size: 14px;
  line-height: 1.6;
  color: #c7cbd3;
  word-break: keep-all;
  & > h2 {
    margin: 0 0 10px;
    font-size: 17px;
    color: #f3f4f6;
  }
  & > ul {
    margin: 0 0 10px;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  & a {
    color: #8fb0ff;
  }
  & > p {
    margin: 0;
  }
`;
